import React, { useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer, SlotInfo, View, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
// Zentrale moment-Locale-Quelle (de + Montag-Wochenstart, SP1) — ersetzt die
// frühere lokale `import "moment/locale/de"` + `moment.locale("de")`-Doppelpflege.
import { ensureMomentDe } from "../utils/momentDe";
import "react-big-calendar/lib/css/react-big-calendar.css";
// Drag&Drop-Addon ist Teil von react-big-calendar (keine neue Abhängigkeit);
// sein eigenes CSS regelt Resize-Anfasser + Preview beim Ziehen.
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
// Zentrales .cal-rd-Override-Stylesheet (Now-Linie, Gutter, Header, Block-Stil,
// „heute"-Spalte, Monatsraster). Ersetzt das früher hier eingebettete <style>;
// einzige Pflegestelle, von TimeGridView/OutlookTermine/Legacy gemeinsam genutzt.
import "./calendar/calendar.css";
import { useResponsive } from "../hooks/useResponsive";
// SP2 (Grid-Lesbarkeit): Serien-/Teams-Indikator + Zeilenlogik des Custom-
// Event-Renderers; Icons wie im TerminFormWindow (Fluent).
import {
    ArrowRepeatAll16Regular,
    TaskListSquareLtr16Regular,
    Video16Regular,
} from "@fluentui/react-icons";
import { isSeriesEvent } from "./calendar/types";
// Farb-Ableitung (ART_FARBE/farbeAusMeta/hellerHintergrund) lebt seit SP4 in
// terminBlock.ts — EINE Quelle für eventPropGetter UND Peek-Popover.
import {
    KALENDER_AKZENT,
    farbeAusMeta,
    hellerHintergrund,
    istTentative,
    vorgangHinweis,
    zeigtZeitzeile,
    zeitOrtZeile,
} from "./calendar/terminBlock";
// SP3 (native All-Day-Zeile + Dedupe-Tooltip): Abwesenheiten gehören in die
// All-Day-Zeile; mehrtägige Ganztagstermine bekommen Verlauf-Optik + Bereich
// im Tooltip; ghostSources (Overlay-Dedupe) erscheinen als „auch in: …".
import { allDayBereichLabel, istAbwesenheitEvent, istMehrtagsAllDay } from "./calendar/allDay";

// Direkt vor dem Localizer-Bau nochmals absichern (idempotent): globale Locale
// 'de' + Wochenregeln dow=1/doy=4 — die Wochen-Range rechnet RBC über
// moment.startOf('week'), also über GENAU diese globalen Regeln.
ensureMomentDe();
const localizer = momentLocalizer(moment);

// Defensiver RBC-Guard (SP1): Der moment-Localizer fällt ohne de-LocaleData auf
// firstOfWeek=0 (Sonntag) zurück (localizers/moment.js: `data ? data.firstDayOfWeek() : 0`).
// ensureMomentDe() macht das praktisch unmöglich; sollte es dennoch passieren,
// klemmen wir den Spaltenstart hart auf Montag und loggen den Zustand — der
// Wochenstart darf nie wieder am Locale-Ladepfad hängen.
const rbcStartOfWeek = localizer.startOfWeek.bind(localizer);
localizer.startOfWeek = (culture) => {
    const dow = rbcStartOfWeek(culture);
    if (dow === 1) return dow;
    // eslint-disable-next-line no-console
    console.warn(`[Kalender] firstOfWeek=${dow} (de-Locale fehlt?) — erzwinge Montag.`);
    return 1;
};

// react-big-calendar mit Drag&Drop-Fähigkeit umhüllen (TER-Plan C, Increment 2).
// Der HOC erweitert die Calendar-Props um onEventDrop/onEventResize/
// draggableAccessor/resizable etc. Generisch über `any`, da unsere View-Events
// kein strenges TEvent-Schema tragen.
const DnDCalendar = withDragAndDrop(Calendar as any);

const SUPPORTED_VIEWS: View[] = [Views.MONTH, Views.WEEK, Views.DAY];

/**
 * Custom-Event-Renderer für die Tag-/Wochen-Zeitraster (SP2, A4/A15/A16-Basis):
 * Zeile 1 = Betreff (fett, ellipsis), Zeile 2 = Zeit/Ort (erst ab 30 min Dauer
 * — darunter ist der Block zu flach), unten rechts Recurring-Icon bei
 * Serienterminen (A15, Outlook-Referenz) + Teams-Kamera-Icon bei
 * Online-Meetings. Ersetzt NUR `components.day/week.event` — die Monatsansicht
 * behält den kompakten RBC-Default (eine Pill-Zeile). Die native
 * `.rbc-event-label`-Zeitzeile wird per CSS ausgeblendet (calendar.css), da
 * dieser Renderer die Zeit selbst anzeigt.
 *
 * `event` bleibt bewusst lose getypt (`any`): der Renderer läuft auch unter den
 * Legacy-Mounts (OutlookTermine, /calender), deren View-Events kein
 * CalendarGridEvent-Schema tragen — fehlende Felder degradieren sanft
 * (kein Icon, keine Zeitzeile).
 *
 * SP4 (Rechtsklick, A14): RBC hat kein natives Kontextmenü-API — der Renderer
 * fängt `onContextMenu` selbst ab (nur wenn ein Handler verdrahtet ist; die
 * Legacy-Mounts behalten das native Browser-Menü).
 */
const TerminBlockInhalt: React.FC<{
    event: any;
    title?: string;
    onContextMenu?: (event: any, pos: { x: number; y: number }) => void;
    /** Schwelle für die Zeit/Ort-Zeile — hängt an der Zeitskala (T-31). */
    zeitzeileAbMinuten?: number;
}> = ({ event, title, onContextMenu, zeitzeileAbMinuten }) => {
    const betreff = title ?? event?.title ?? "";
    // Ganztagstermine (native All-Day-Zeile, SP3) zeigen KEINE Zeitzeile —
    // „00:00 – 00:00" wäre sinnfrei; die flache Leiste hat ohnehin nur 1 Zeile.
    const zeile2 =
        !event?.allDay &&
        event?.start instanceof Date &&
        event?.end instanceof Date &&
        zeigtZeitzeile(event.start, event.end, zeitzeileAbMinuten)
            ? zeitOrtZeile(event.start, event.end, event?.location)
            : null;
    const serie = isSeriesEvent({ type: event?.type, seriesMasterId: event?.seriesMasterId });
    const teams = !!event?.onlineMeetingUrl;
    // K9: hängt der Termin an einem Vorgang? Legacy-Mounts ohne meta bleiben
    // unverändert — das Icon erscheint nur, wenn die Sidecar-Meta da ist.
    const amVorgang = event?.meta?.vorgangId != null;
    const icons = serie || teams || amVorgang;
    return (
        <div
            className={`cal-ev${icons && !zeile2 ? " cal-ev-kompakt" : ""}`}
            onContextMenu={
                onContextMenu
                    ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onContextMenu(event, { x: e.clientX, y: e.clientY });
                      }
                    : undefined
            }
        >
            <div className="cal-ev-titel">{betreff}</div>
            {/* PO 28.09.2026: Ebene „Termine meiner Kontakte" — über wen. */}
            {event?.ueberKontakt?.name && (
                <div className="cal-ev-kontakt-name">über {event.ueberKontakt.name}</div>
            )}
            {zeile2 && <div className="cal-ev-zeit">{zeile2}</div>}
            {icons && (
                <div className="cal-ev-icons" aria-hidden="true">
                    {teams && <Video16Regular />}
                    {serie && <ArrowRepeatAll16Regular />}
                    {amVorgang && <TaskListSquareLtr16Regular className="cal-ev-vorgang" />}
                </div>
            )}
        </div>
    );
};

const messages = {
    today: "Heute",
    previous: "Zurück",
    next: "Weiter",
    month: "Monat",
    week: "Woche",
    day: "Tag",
    agenda: "Agenda",
    date: "Datum",
    time: "Uhrzeit",
    event: "Termin",
    allDay: "Ganztägig",
    noEventsInRange: "Keine Termine in diesem Zeitraum.",
    showMore: (total: number) => `+ ${total} weitere`,
};

/**
 * Zentrale react-big-calendar-Formatierung — erzwingt durchgängig DEUTSCH +
 * 24-Stunden-Zeit (HH:mm, KEIN AM/PM) für ALLE Zeit- und Header-Slots. Ohne
 * dieses `formats`-Objekt fällt RBC auf die moment-Default-Strings zurück, die
 * je nach Slot 12-Stunden-/englische Ausgaben zeigen.
 *
 * Alle Zeit-Slots → `HH:mm`; alle Datums-/Header-Slots → deutsche Tokens
 * (DD.MM.YYYY, MMMM YYYY, ddd …). Da `ensureMomentDe()` oben die globale
 * de-Locale erzwingt, liefern die ausgeschriebenen Tokens (MMMM, dddd, ddd)
 * die deutschen Namen.
 *
 * Diese eine Definition versorgt JEDEN RBC-Mount, der über OutlookKalender
 * läuft: /termine (TimeGridView + SharedColumnsView) sowie die Legacy-Mounts
 * (OutlookTermine, /calender).
 */
const formats = {
    // Zeit-Gutter (linke Stundenspalte in Tag/Woche): 09:00, 10:00 …
    timeGutterFormat: (date: Date, _culture: string | undefined, loc: any) =>
        loc.format(date, "HH:mm", "de"),
    // Zeitbereich eines Events (z. B. „09:00 – 10:30").
    eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }, _culture: string | undefined, loc: any) =>
        `${loc.format(start, "HH:mm", "de")} – ${loc.format(end, "HH:mm", "de")}`,
    // Nur-Start-Variante (Event reicht über den sichtbaren Bereich hinaus).
    eventTimeRangeStartFormat: ({ start }: { start: Date }, _culture: string | undefined, loc: any) =>
        `${loc.format(start, "HH:mm", "de")} – `,
    // Nur-Ende-Variante.
    eventTimeRangeEndFormat: ({ end }: { end: Date }, _culture: string | undefined, loc: any) =>
        ` – ${loc.format(end, "HH:mm", "de")}`,
    // Aufgezogene Auswahl (Drag-to-Create): „22.06.2026 09:00 – 09:30".
    selectRangeFormat: ({ start, end }: { start: Date; end: Date }, _culture: string | undefined, loc: any) =>
        `${loc.format(start, "HH:mm", "de")} – ${loc.format(end, "HH:mm", "de")}`,
    // Wochen-/Bereichs-Kopf der Toolbar: „22.06.2026 – 28.06.2026".
    dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }, _culture: string | undefined, loc: any) =>
        `${loc.format(start, "DD.MM.YYYY", "de")} – ${loc.format(end, "DD.MM.YYYY", "de")}`,
    // Toolbar-Kopf der Tagesansicht: „Mo. 22.06.".
    dayHeaderFormat: (date: Date, _culture: string | undefined, loc: any) =>
        loc.format(date, "ddd DD.MM.", "de"),
    // Spaltenkopf je Tag in der TAG-/WOCHEN-Ansicht — RBC nutzt dafür
    // `dayFormat` (TimeGridHeader), NICHT `dayHeaderFormat`. Vorher stand hier
    // nur "DD" (fälschlich als Monatsraster-Format kommentiert) → der
    // Wochenkopf zeigte nackte Tageszahlen ohne Wochentagsnamen (SP1-Fix).
    dayFormat: (date: Date, _culture: string | undefined, loc: any) =>
        loc.format(date, "ddd DD.MM.", "de"),
    // Tageszahl in der Monatsraster-Zelle (dafür gilt `dateFormat`).
    dateFormat: (date: Date, _culture: string | undefined, loc: any) =>
        loc.format(date, "DD", "de"),
    // Wochentags-Kürzel über dem Monatsraster: „Mo", „Di" …
    weekdayFormat: (date: Date, _culture: string | undefined, loc: any) =>
        loc.format(date, "ddd", "de"),
    // Monats-Kopf der Toolbar: „Juni 2026".
    monthHeaderFormat: (date: Date, _culture: string | undefined, loc: any) =>
        loc.format(date, "MMMM YYYY", "de"),
    // Agenda-Ansicht — Zeitspalte (24h).
    agendaTimeFormat: (date: Date, _culture: string | undefined, loc: any) =>
        loc.format(date, "HH:mm", "de"),
    // Agenda-Zeitbereich (24h).
    agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }, _culture: string | undefined, loc: any) =>
        `${loc.format(start, "HH:mm", "de")} – ${loc.format(end, "HH:mm", "de")}`,
    // Agenda-Kopfbereich (Datumsspanne): „22.06.2026 – 28.06.2026".
    agendaHeaderFormat: ({ start, end }: { start: Date; end: Date }, _culture: string | undefined, loc: any) =>
        `${loc.format(start, "DD.MM.YYYY", "de")} – ${loc.format(end, "DD.MM.YYYY", "de")}`,
    // Agenda-Datum je Zeile: „Mo 22.06.2026".
    agendaDateFormat: (date: Date, _culture: string | undefined, loc: any) =>
        loc.format(date, "ddd DD.MM.YYYY", "de"),
};

/** Argumente eines Drag- bzw. Resize-Vorgangs (vom DnD-Addon geliefert). */
export interface KalenderEventChange {
    event: any;
    start: Date;
    end: Date;
    isAllDay?: boolean;
}

interface OutlookKalenderProps {
    events: any[];
    date: Date;
    onNavigate: (date: Date) => void;
    /**
     * Einzelklick auf einen Termin. Das zweite Argument ist das RBC-Klick-Event
     * (SP4): der Kalender-Neubau leitet daraus den Anker des Peek-Popovers ab
     * (`.rbc-event`-Blockrechteck). Legacy-Mounts ignorieren es gefahrlos.
     */
    onSelectEvent: (event: any, e?: React.SyntheticEvent) => void;
    /**
     * Doppelklick auf einen Termin (SP4): öffnet im Neubau weiterhin DIREKT das
     * Termin-Fenster (Einzelklick zeigt nur noch das Peek-Popover, A16).
     */
    onDoubleClickEvent?: (event: any) => void;
    /**
     * Rechtsklick auf einen Terminblock (SP4, A14): kleines Kontextmenü
     * („Verschieben", „Teams-Meeting teilnehmen"). Nur im Tag-/Wochen-Zeitraster
     * (Custom-Event-Renderer); ohne Handler bleibt das native Browser-Menü.
     */
    onEventContextMenu?: (event: any, pos: { x: number; y: number }) => void;
    /**
     * Wird beim Ziehen eines Termins (Verschieben) aufgerufen. Ist der Handler
     * nicht gesetzt, bleibt Drag&Drop deaktiviert (Legacy-Mount, z. B. /calender).
     */
    onEventDrop?: (change: KalenderEventChange) => void;
    /** Wird beim Größenändern (Resize, Anpassen Start/Ende) aufgerufen. */
    onEventResize?: (change: KalenderEventChange) => void;
    /**
     * Entscheidet, ob ein einzelnes Event ziehbar/resizebar ist. Mit dieser
     * Funktion erzwingen wir das Schreibrecht: nur Termine im eigenen Kalender
     * sind ziehbar, fremde/geteilte (read-only) nicht (Outlook-Verhalten).
     */
    canEditEvent?: (event: any) => boolean;
    /**
     * Leerer Slot angeklickt/aufgezogen (Drag-to-Create, SP3). Wird der Handler
     * gesetzt, schaltet das RBC `selectable` ein. SP1 reicht ihn nur durch.
     */
    onSelectSlot?: (slot: SlotInfo) => void;
    /**
     * View extern kontrollieren statt intern via useState. Ist `view` gesetzt,
     * gewinnt der externe Wert (controlled) — so steuert TimeGridView den
     * Kalender über den CalendarStateContext. Ohne Prop bleibt das alte
     * (uncontrolled) Verhalten für die Legacy-Mounts (OutlookTermine, /calender).
     */
    view?: View;
    onView?: (v: View) => void;
    /**
     * Interne RBC-Toolbar (Heute/prev/next/View) ein-/ausschalten. SP1-Default
     * für /termine = false (eigene Top-Leiste). Default `true` hält die
     * Legacy-/calender-Route + OutlookTermine rückwärtskompatibel.
     */
    toolbar?: boolean;
    /**
     * Zeitskala des Tag-/Wochenrasters (T-31, `rasterFuerSkala`): Slot-Länge in
     * Minuten (bestimmt auch das Einrasten beim Ziehen), Slots je Stunde,
     * Stundenhöhe in px und die Schwelle für die Zeit/Ort-Zeile. Ohne Props
     * bleibt das bisherige Raster (15 min, 4 Slots, 88px) — Legacy-Mounts
     * sehen unverändert aus.
     */
    step?: number;
    timeslots?: number;
    stundePx?: number;
    zeitzeileAbMinuten?: number;
}

const OutlookKalender: React.FC<OutlookKalenderProps> = ({
    events,
    date,
    onNavigate,
    onSelectEvent,
    onDoubleClickEvent,
    onEventContextMenu,
    onEventDrop,
    onEventResize,
    canEditEvent,
    onSelectSlot,
    view: viewProp,
    onView: onViewProp,
    toolbar,
    step = 15,
    timeslots = 4,
    stundePx = 88,
    zeitzeileAbMinuten,
}) => {
    const { isPhone } = useResponsive();
    // Auf dem Phone ist das Wochen-/Monatsgitter kaum bedienbar → standardmäßig
    // die Tages-Ansicht (kompakte Agenda eines Tages). Die User-Auswahl bleibt
    // erhalten; nur der initiale/beim Wechsel-auf-Phone gesetzte Default folgt
    // dem Viewport. Dieser interne State greift NUR im uncontrolled Modus
    // (kein `view`-Prop) — controlled gewinnt der externe Wert.
    const [internalView, setInternalView] = useState<View>(isPhone ? Views.DAY : Views.MONTH);
    // Bewusste User-Auswahl markieren, damit sie nicht vom Phone-Default
    // überschrieben wird.
    const [userPicked, setUserPicked] = useState(false);
    const controlled = viewProp !== undefined;
    const view: View = controlled ? viewProp : internalView;

    useEffect(() => {
        // Solange der Nutzer die View nicht selbst gewählt hat, dem Viewport
        // folgen: Phone → Tag, sonst → Monat. Nur im uncontrolled Modus.
        if (controlled || userPicked) return;
        setInternalView(isPhone ? Views.DAY : Views.MONTH);
    }, [isPhone, userPicked, controlled]);

    // Farbe je Termin-Art (TER-D5): klassifizierte Events nach Art einfärben;
    // unklassifizierte fallen auf die Kalender-Farbe der Quelle zurück (sonst
    // Akzentfarbe). Outlook-Kategorien-Optik: heller Hintergrund + farbiger
    // linker Balken + dunkle Schrift (statt Vollflächen-Weiß-auf-Farbe).
    // Pflicht-Blöcke (geschützt) bekommen zusätzlich einen gestrichelten Rand.
    // Drag&Drop nur aktiv, wenn Persistenz-Handler übergeben wurden (Legacy-Mount
    // /calender hat keine → bleibt rein lesend). `dndAktiv` schaltet das gesamte
    // DnD-Verhalten (Calendar-Variante + Klassen + Anfasser) gemeinsam.
    const dndAktiv = !!(onEventDrop || onEventResize);

    // Schreibrecht je Event: nur eigene (beschreibbare) Termine sind ziehbar.
    // Ohne `canEditEvent` (oder ohne DnD) gilt nichts als ziehbar.
    const istEditierbar = useMemo(
        () => (event: any): boolean => (dndAktiv && canEditEvent ? canEditEvent(event) : false),
        [dndAktiv, canEditEvent],
    );

    const eventPropGetter = (event: any) => {
        const artColor = farbeAusMeta(event?.meta);
        const akzent: string = artColor || event?.source?.color || KALENDER_AKZENT;
        const geschuetzt = !!event?.meta?.pflichtBlock;
        // „Mit Vorbehalt" (Graph showAs=tentative, A6): gestrichelter Rand +
        // abgeschwächter Hintergrund — sofort von fest zugesagten Terminen
        // unterscheidbar (Outlook-Optik).
        const tentative = istTentative(event?.showAs);
        // Ganztags-Optik (native All-Day-Zeile, SP3): mehrtägige Verläufe als
        // durchgezogene, kräftigere Leiste; eintägige Ganztagstermine als
        // kompakte Pille — beides sofort unterscheidbar (A2).
        const ganztags = !!event?.allDay;
        const mehrtags =
            ganztags &&
            event?.start instanceof Date &&
            event?.end instanceof Date &&
            istMehrtagsAllDay(event.start, event.end);
        // Fremde (read-only) Termine zusätzlich per Klasse kennzeichnen, damit
        // das Cursor-/Anfasser-CSS sie als nicht-ziehbar zeigt.
        const readOnly = dndAktiv && !istEditierbar(event);
        // Ebene „Termine meiner Kontakte" (PO 28.09.2026): fremder Kalender,
        // nur ansehen — rundum gestrichelt und zurückgenommen.
        const ueberKontakt = !!event?.ueberKontakt;
        const klassen = [readOnly ? "kvp-rbc-readonly" : "", ueberKontakt ? "cal-ev-kontakt" : ""]
            .filter(Boolean)
            .join(" ");
        return {
            className: klassen || undefined,
            style: {
                backgroundColor: hellerHintergrund(akzent, tentative ? 0.06 : mehrtags ? 0.24 : 0.14),
                borderLeft: `3px ${tentative ? "dashed" : "solid"} ${akzent}`,
                // Weiße 1px-Ränder statt "none" (SP2/A5): parallele Kacheln im
                // no-overlap-Layout bekommen einen sichtbaren Spalt zueinander.
                borderTop: "1px solid #fff",
                borderRight: "1px solid #fff",
                borderBottom: "1px solid #fff",
                color: "#222",
                borderRadius: ganztags ? (mehrtags ? 3 : 999) : 4,
                boxShadow: "none",
                ...(tentative
                    ? { borderTop: `1px dashed ${akzent}`, borderRight: `1px dashed ${akzent}`, borderBottom: `1px dashed ${akzent}` }
                    : {}),
                ...(geschuetzt
                    ? { borderTop: `1px dashed ${akzent}`, borderRight: `1px dashed ${akzent}`, borderBottom: `1px dashed ${akzent}`, opacity: 0.95 }
                    : {}),
                ...(ueberKontakt
                    ? {
                          backgroundColor: "#ffffff",
                          borderTop: `1px dashed ${akzent}`,
                          borderRight: `1px dashed ${akzent}`,
                          borderBottom: `1px dashed ${akzent}`,
                          borderLeft: `3px dashed ${akzent}`,
                          opacity: 0.9,
                      }
                    : {}),
            },
        };
    };

    // Tooltip (title-Attribut): voller Betreff + Datumsbereich bei mehrtägigen
    // Ganztagsterminen (A2) + „auch in: …" für Overlay-Dubletten (SP3,
    // Entscheidung 62 — die Dedupe-Verlierer-Quellen aus ghostSources).
    const tooltipAccessor = (event: any): string => {
        const teile: string[] = [event?.title || "(ohne Titel)"];
        if (
            event?.allDay &&
            event?.start instanceof Date &&
            event?.end instanceof Date &&
            istMehrtagsAllDay(event.start, event.end)
        ) {
            teile.push(allDayBereichLabel(event.start, event.end));
        }
        const ghosts: { address?: string; displayName?: string }[] = Array.isArray(
            event?.ghostSources,
        )
            ? event.ghostSources
            : [];
        if (ghosts.length > 0) {
            teile.push(`auch in: ${ghosts.map((g) => g.displayName || g.address || "?").join(", ")}`);
        }
        if (event?.ueberKontakt?.name) {
            teile.push(`Termin meines Kontakts: ${event.ueberKontakt.name} (nur ansehen)`);
        }
        // K9: der Vorgang am Termin — eine Quelle für Grid und Peek.
        const vorgang = vorgangHinweis(event?.meta);
        if (vorgang) teile.push(vorgang);
        return teile.join("\n");
    };

    // Custom-Event-Renderer (SP2) — mit optionalem Kontextmenü-Durchgriff (SP4):
    // der Renderer bekommt den Handler als Prop, weil RBC kein natives
    // onContextMenu-API für Events hat. Memoisiert, damit RBC die Komponente
    // nicht bei jedem Render neu mountet.
    const eventComponents = useMemo(() => {
        const Inhalt: React.FC<{ event: any; title?: string }> = (props) => (
            <TerminBlockInhalt
                {...props}
                onContextMenu={onEventContextMenu}
                zeitzeileAbMinuten={zeitzeileAbMinuten}
            />
        );
        return { day: { event: Inhalt }, week: { event: Inhalt } };
    }, [onEventContextMenu, zeitzeileAbMinuten]);

    // Das DnD-Addon liefert start/end als `Date | string`; auf `Date`
    // normalisieren und nur weiterreichen, wenn das Event auch editierbar ist
    // (Doppelsicherung neben draggableAccessor/resizableAccessor).
    const handleDrop = (args: any) => {
        if (!onEventDrop || !istEditierbar(args?.event)) return;
        onEventDrop({
            event: args.event,
            start: new Date(args.start),
            end: new Date(args.end),
            isAllDay: args.isAllDay,
        });
    };
    const handleResize = (args: any) => {
        if (!onEventResize || !istEditierbar(args?.event)) return;
        onEventResize({
            event: args.event,
            start: new Date(args.start),
            end: new Date(args.end),
            isAllDay: args.isAllDay,
        });
    };

    // Gemeinsame Props für Calendar (beide Varianten identisch). Bei aktivem DnD
    // wird die HOC-Variante mit den Drag/Resize-Props gerendert.
    const commonProps = {
        localizer,
        culture: "de",
        messages,
        formats,
        events,
        startAccessor: "start" as const,
        endAccessor: "end" as const,
        titleAccessor: (event: any) =>
            // Pflicht-Blöcke (geschützt) mit Schloss-Präfix kennzeichnen.
            event?.meta?.pflichtBlock ? `🔒 ${event.title ?? ""}` : event?.title ?? "",
        date,
        onNavigate,
        view,
        onView: (v: View) => {
            // Controlled: nach außen melden (CalendarStateContext entscheidet).
            // Uncontrolled (Legacy): internen State + User-Auswahl setzen.
            if (controlled) {
                onViewProp?.(v);
            } else {
                setUserPicked(true);
                setInternalView(v);
            }
        },
        views: SUPPORTED_VIEWS,
        onSelectEvent,
        // Doppelklick → direkt das Termin-Fenster (SP4); Einzelklick zeigt im
        // Neubau nur noch das Peek-Popover. Ohne Handler (Legacy) kein Effekt.
        onDoubleClickEvent,
        // Slot-Auswahl (Drag-to-Create, SP3) nur aktiv, wenn ein Handler
        // übergeben wurde. `'ignoreEvents'` statt `true`: die Hintergrund-
        // Selektion zieht sauber über bestehende Events hinweg und kollidiert
        // nicht mit dem Event-Drag des DnD-Addons (Konsistenz-Review §11-Risiko;
        // Event-Drag bleibt onEventDrop, Hintergrund bleibt onSelectSlot). SP1
        // lässt onSelectSlot leer → selectable bleibt aus (Legacy /calender).
        selectable: onSelectSlot ? ("ignoreEvents" as const) : false,
        onSelectSlot,
        // Slot-Granularität für Drag-to-Create (SP3): Default 15-min-Raster,
        // 4 Slots je Stundengruppe → volle Stunde bleibt als Gruppe sichtbar.
        // Seit T-31 über die Zeitskala einstellbar (step * timeslots = 60).
        step,
        timeslots,
        // Touch (SP3-Grundlage, Feintuning SP6): Long-Press statt sofortiger
        // Selektion → kein Konflikt mit vertikalem Scroll.
        longPressThreshold: 250,
        // RBC-interne Toolbar: Default an (Legacy), für /termine via Prop aus.
        toolbar: toolbar ?? true,
        eventPropGetter,
        tooltipAccessor,
        // Native All-Day-Zeile (SP3, Entscheidung 61): ganztägige Events UND
        // Abwesenheiten (auch zeitgebundene, z. B. halbtags krank) laufen in die
        // RBC-All-Day-Zeile — mehrtägige spannen dort nativ über die Tagesspalten.
        // Ersetzt den früheren splitAllDay/AllDayBar-Pfad; die Legacy-Mounts
        // filtern Ganztägige weiterhin selbst heraus (Accessor greift dort nie).
        allDayAccessor: (event: any) =>
            !!event?.allDay ||
            istAbwesenheitEvent({
                title: event?.title ?? "",
                allDay: !!event?.allDay,
                meta: event?.meta,
            }),
        // Max. 3 Ganztags-Zeilen pro Tag; darüber zeigt RBC „+N weitere" und
        // öffnet (popup: true) das Overlay mit allen Einträgen des Tages.
        allDayMaxRows: 3,
        // Parallele Termine als getrennte Spalten-Kacheln statt überlappender
        // Stapel (SP2/A5) — zusammen mit den weißen 1px-Rändern aus dem
        // eventPropGetter entsteht ein klarer Spalt zwischen den Kacheln.
        dayLayoutAlgorithm: "no-overlap" as const,
        // Custom-Event-Renderer NUR für die Zeitraster (Tag/Woche, SP2) — die
        // Monatsansicht behält den kompakten RBC-Default. Seit SP4 memoisiert
        // inkl. Kontextmenü-Durchgriff (s. eventComponents oben).
        components: eventComponents,
        popup: true,
        scrollToTime: new Date(1970, 1, 1, 7, 0, 0),
        style: { height: isPhone ? "70vh" : "75vh", minHeight: isPhone ? 420 : 560 },
    };

    return (
        <div
            className={dndAktiv ? "cal-rd cal-rd-dnd" : "cal-rd"}
            style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background: "#fff",
                borderRadius: 8,
                border: "1px solid #e0e0e0",
                padding: isPhone ? 6 : 12,
                boxSizing: "border-box",
                // Stundenhöhe fürs Raster (calendar.css, .rbc-timeslot-group).
                ["--cal-stunde-px" as string]: `${stundePx}px`,
            }}
        >
            {/* Optik kommt komplett aus calendar.css (.cal-rd .rbc-*); kein
                Inline-<style> mehr (Doppelpflege vermieden, SP1). */}
            {dndAktiv ? (
                <DnDCalendar
                    {...(commonProps as any)}
                    onEventDrop={handleDrop}
                    onEventResize={handleResize}
                    resizable
                    // Nur eigene (beschreibbare) Termine ziehen/resizen; fremde
                    // (read-only) bleiben anklickbar, aber unverrückbar.
                    draggableAccessor={(event: any) => istEditierbar(event)}
                    resizableAccessor={(event: any) => istEditierbar(event)}
                />
            ) : (
                <Calendar {...(commonProps as any)} />
            )}
        </div>
    );
};

export default OutlookKalender;
